import type { Graphics } from 'pixi.js';

/**
 * Offense glyphs (power, tempo, supershot, weapon categories) — every
 * silhouette unique so a card reads without its text (docs/03 W4).
 * Drawn in a ±18 unit box; the caller scales.
 */

/** Dark punch-out colour for inner details (matches card sockets). */
export const GLYPH_INK = 0x0f0c1a;

export function chevron(g: Graphics, x: number, y: number, color: number): void {
  g.poly([x - 14, y + 6, x, y - 8, x + 14, y + 6, x + 9, y + 10, x, y + 0, x - 9, y + 10])
    .fill(color);
}

/** Weapon-category glyph (bullet capsules / orb / beam / bomb). */
function category(g: Graphics, cat: string, c: number): boolean {
  switch (cat) {
    case 'bullet': // three slanted bullet capsules
      for (const [dx, dy] of [[-12, 6], [0, 0], [12, -6]] as const) {
        g.roundRect(dx - 4, dy - 10, 8, 20, 4).fill(c);
      }
      return true;
    case 'plasma': // orb with halo ring
      g.circle(0, 0, 9).fill(c);
      g.circle(0, 0, 15).stroke({ color: c, width: 3, alpha: 0.6 });
      return true;
    case 'laser': // beam with emitter
      g.roundRect(-18, -3, 30, 6, 3).fill(c);
      g.poly([12, -8, 20, 0, 12, 8]).fill(c);
      g.moveTo(-18, -8).lineTo(-18, 8).stroke({ color: c, width: 4, cap: 'round' });
      return true;
    case 'bomb': // bomb sphere + fuse spark
      g.circle(-2, 4, 11).fill(c);
      g.roundRect(2, -9, 5, 7, 2).fill(c);
      g.circle(8, -13, 3.5).fill(0xffffff);
      return true;
  }
  return false;
}

/**
 * Category DAMAGE / FIRE RATE glyph: the category shape shrunk to the
 * upper left + a corner badge — up-chevron = damage, double » = rate.
 * The two used to share one glyph (audit W4).
 */
function categoryStat(g: Graphics, cat: string, rate: boolean, c: number): boolean {
  g.save();
  g.translateTransform(-4, -4).scaleTransform(0.78, 0.78);
  const ok = category(g, cat, c);
  g.restore();
  if (!ok) return false;
  g.circle(11, 11, 9).fill(GLYPH_INK);
  if (rate) {
    for (const dx of [-3, 4] as const) {
      g.poly([7 + dx, 5, 13 + dx, 11, 7 + dx, 17, 4 + dx, 17, 10 + dx, 11, 4 + dx, 5]).fill(c);
    }
  } else {
    g.poly([3, 15, 11, 6, 19, 15, 16, 18, 11, 12, 6, 18]).fill(c);
  }
  return true;
}

export function drawCombatGlyph(g: Graphics, icon: string, c: number): boolean {
  if (icon.startsWith('cat-')) return category(g, icon.slice(4), c);
  if (icon.startsWith('dmg-')) return categoryStat(g, icon.slice(4), false, c);
  if (icon.startsWith('rate-')) return categoryStat(g, icon.slice(5), true, c);
  switch (icon) {
    case 'damage': // upward double chevron (more power)
      chevron(g, 0, -6, c);
      chevron(g, 0, 8, c);
      return true;
    case 'firerate': // revolver cylinder — rounds per second
      g.circle(0, 0, 16).fill(c);
      for (let i = 0; i < 3; i++) {
        const a = (Math.PI * 2 * i) / 3 - Math.PI / 2;
        g.circle(Math.cos(a) * 8, Math.sin(a) * 8, 4.5).fill(GLYPH_INK);
      }
      g.circle(0, 0, 2.5).fill(GLYPH_INK);
      return true;
    case 'projspeed': // dart with trailing speed lines
      g.poly([18, 0, 2, -7, 6, 0, 2, 7]).fill(c);
      for (const [x, w] of [[-4, 10], [-10, 7], [-16, 4]] as const) {
        g.roundRect(x - w, -2, w, 4, 2).fill({ color: c, alpha: 0.6 });
      }
      return true;
    case 'range': // span arrow between end stops (longer reach)
      g.moveTo(-17, -10).lineTo(-17, 10).stroke({ color: c, width: 4, cap: 'round' });
      g.moveTo(17, -10).lineTo(17, 10).stroke({ color: c, width: 4, cap: 'round' });
      g.roundRect(-12, -2.5, 24, 5, 2.5).fill(c);
      g.poly([10, -7, 17, 0, 10, 7]).fill(c);
      return true;
    case 'critchance': // crosshair with strike spark
      g.circle(0, 0, 12).stroke({ color: c, width: 3.5 });
      for (const [dx, dy] of [[0, -1], [0, 1], [-1, 0], [1, 0]] as const) {
        g.moveTo(dx * 12, dy * 12).lineTo(dx * 18, dy * 18)
          .stroke({ color: c, width: 3.5, cap: 'round' });
      }
      g.poly([0, -5, 4, 0, 0, 5, -4, 0]).fill(c);
      return true;
    case 'critdamage': // jagged impact star
      g.poly([0, -18, 5, -6, 17, -8, 8, 1, 14, 12, 2, 8, -4, 18, -6, 6, -17, 5, -8, -3])
        .fill(c);
      g.poly([0, -7, 3, 0, 0, 7, -3, 0]).fill(GLYPH_INK);
      return true;
    case 'overcharge': // core with radiating ticks
      g.circle(0, 0, 8).fill(c);
      for (let i = 0; i < 8; i++) {
        const a = (Math.PI / 4) * i;
        g.moveTo(Math.cos(a) * 11, Math.sin(a) * 11)
          .lineTo(Math.cos(a) * 17, Math.sin(a) * 17)
          .stroke({ color: c, width: 4, cap: 'round' });
      }
      return true;
    case 'supershot': // charged comet — big round shot with a tail
      g.poly([-17, 17, -4, 0, 2, 6]).fill({ color: c, alpha: 0.55 });
      g.poly([-12, 17, -2, 4, 4, 9]).fill({ color: c, alpha: 0.35 });
      g.circle(5, -5, 11).fill(c);
      g.circle(8, -8, 4).fill(0xffffff);
      return true;
    case 'superfan': // fan of five darts spreading from one muzzle
      g.circle(0, 13, 4).fill(c);
      for (let i = 0; i < 5; i++) {
        const a = -Math.PI / 2 + (i - 2) * 0.5;
        const x = Math.cos(a) * 19;
        const y = 13 + Math.sin(a) * 19;
        const px = Math.cos(a + Math.PI / 2) * 3.5;
        const py = Math.sin(a + Math.PI / 2) * 3.5;
        const bx = Math.cos(a) * 8;
        const by = 13 + Math.sin(a) * 8;
        g.poly([x, y, bx + px, by + py, bx - px, by - py]).fill(c);
      }
      return true;
    case 'energy': // lightning bolt
      g.poly([2, -18, -10, 2, -2, 2, -4, 18, 10, -4, 2, -4]).fill(c);
      return true;
    case 'barrel': // gun barrel + muzzle
      g.roundRect(-16, -4, 24, 8, 4).fill(c);
      g.poly([14, -8, 22, 0, 14, 8]).fill(c);
      return true;
    case 'rack': // two stacked barrels (dual weapon)
      g.roundRect(-16, -10, 28, 7, 3.5).fill(c);
      g.roundRect(-16, 3, 28, 7, 3.5).fill(c);
      g.poly([14, -10, 20, -6.5, 14, -3]).fill(c);
      g.poly([14, 3, 20, 6.5, 14, 10]).fill(c);
      return true;
  }
  return false;
}
